import { createResourceKeys } from "@/lib/resource-context";
import type { ToolExecutionsFilters } from "../search";

type ListKeyParams = {
  organizationId: string;
  projectId: string;
  filters: ToolExecutionsFilters;
  limit: number;
  offset: number;
};

type DetailKeyParams = {
  organizationId: string;
  projectId: string;
  id: string;
  includeInputs: boolean;
  includeOutputs: boolean;
};

const resourceKeys = createResourceKeys("tool_executions");

/**
 * Built on the shared factory so the keys sit under the `resources` namespace
 * that prefix-based invalidation walks.
 *
 * Filters and pagination are appended to the list key, and the include flags to
 * the detail key, because each one changes the request the engine sees.
 */
export const toolExecutionsKeys = {
  ...resourceKeys,
  settings: ({
    organizationId,
    projectId,
  }: Pick<ListKeyParams, "organizationId" | "projectId">) =>
    [
      ...resourceKeys.single({ scope: { organizationId, projectId } }),
      "settings",
    ] as const,
  list: ({
    organizationId,
    projectId,
    filters,
    limit,
    offset,
  }: ListKeyParams) =>
    [
      ...resourceKeys.list({ scope: { organizationId, projectId } }),
      { filters, limit, offset },
    ] as const,
  detail: ({
    organizationId,
    projectId,
    id,
    includeInputs,
    includeOutputs,
  }: DetailKeyParams) =>
    [
      ...resourceKeys.detail({ scope: { organizationId, projectId }, id }),
      { includeInputs, includeOutputs },
    ] as const,
  newerHead: ({
    organizationId,
    projectId,
    filters,
    limit,
  }: Omit<ListKeyParams, "offset">) =>
    [
      ...resourceKeys.list({ scope: { organizationId, projectId } }),
      "newer-head",
      { filters, limit },
    ] as const,
};
